import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import {
  DialBlockCampaignI,
  DialBlockI,
  DIAL_BLOCK_STATUSES,
} from "@/api/routes/dial-blocks";

import { DIAL_BLOCK_ET_ZONE, DIAL_BLOCK_LENGTH_MINUTES } from "./constants";
import { getDialBlockEnd } from "./format";

/**
 * The caller's week, all in ET (the business clock). Days are YYYY-MM-DD keys;
 * times of day are minutes since ET midnight. Weeks are Monday to Saturday,
 * plus Sunday for a caller with a campaign whose LGA turned Sunday blocks on
 * (C-16 S8).
 */

/** First row of the grid, 8:00 AM ET: no block may start earlier. */
export const GRID_START_MINUTES = 8 * 60;

/** End of the grid, 9:00 PM ET: no block may end later. */
export const GRID_END_MINUTES = 21 * 60;

export const SLOT_MINUTES = 30;

/** The last start that still ends by 9 PM ET: 5:30 PM. */
export const LAST_START_MINUTES = GRID_END_MINUTES - DIAL_BLOCK_LENGTH_MINUTES;

/** Monday to Saturday; Sunday is added only when a campaign takes it. */
export const WEEK_DAY_COUNT = 6;

/**
 * Whether any of the caller's campaigns takes Sunday blocks: the backend
 * lists Sunday (7) in `call_days` only once the campaign's LGA turned it on.
 */
export const takesSundayBlocks = (
  campaigns?: Array<Pick<DialBlockCampaignI, "call_days">>
): boolean => !!campaigns?.some(({ call_days }) => call_days?.includes(7));

/** The 26 half-hour rows, 8:00 AM to 8:30 PM ET, as minutes since midnight. */
export const GRID_SLOTS: Array<number> = Array.from(
  { length: (GRID_END_MINUTES - GRID_START_MINUTES) / SLOT_MINUTES },
  (_, i) => GRID_START_MINUTES + i * SLOT_MINUTES
);

/** Clear Week keeps blocks starting within this many minutes (CC's rule). */
export const CLEAR_WEEK_BUFFER_MINUTES = 30;

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const pad = (value: number) => String(value).padStart(2, "0");

const toUtcDate = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);

  return new Date(Date.UTC(year, month - 1, day));
};

/** Calendar arithmetic on day keys, done in UTC so no zone can shift it. */
export const addDaysToKey = (key: string, days: number): string => {
  const date = toUtcDate(key);

  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

/** 1 = Monday … 7 = Sunday. */
export const getIsoWeekday = (key: string) => {
  const day = toUtcDate(key).getUTCDay();

  return day === 0 ? 7 : day;
};

export const isValidDayKey = (value: string): boolean =>
  DAY_KEY_PATTERN.test(value) &&
  toUtcDate(value).toISOString().slice(0, 10) === value;

/** The ET day key of an instant. */
export const getEtDayKey = (at: Date): string =>
  formatInTimeZone(at, DIAL_BLOCK_ET_ZONE, "yyyy-MM-dd");

/** An instant's ET day and its minutes since ET midnight. */
export const getEtDayAndMinutes = (at: Date) => ({
  dayKey: getEtDayKey(at),
  minutes:
    Number(formatInTimeZone(at, DIAL_BLOCK_ET_ZONE, "H")) * 60 +
    Number(formatInTimeZone(at, DIAL_BLOCK_ET_ZONE, "m")),
});

/** The instant of an ET wall-clock time on an ET day (DST-aware). */
export const getEtInstant = (dayKey: string, minutes: number): Date =>
  fromZonedTime(
    `${dayKey}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`,
    DIAL_BLOCK_ET_ZONE
  );

/**
 * The Monday of the week a day belongs to, Sunday included: the backend's
 * `week_start` (Monday 00:00 ET to the next Monday 00:00 ET).
 */
export const getWeekStartForDay = (key: string): string =>
  addDaysToKey(key, 1 - getIsoWeekday(key));

/**
 * The week "Today" opens on: this ET week, moving on to next week from
 * Saturday 11:59 PM ET and all of Sunday (Command Center's rule: nothing can
 * be booked on a Sunday, so a caller looking then is planning ahead). A
 * caller with Sunday blocks (`includeSunday`) stays on this week to its end.
 */
export const getCurrentWeekStart = (
  now: Date,
  includeSunday = false
): string => {
  const today = getEtDayKey(now);
  const weekday = getIsoWeekday(today);

  if (
    !includeSunday &&
    (weekday === 7 ||
      (weekday === 6 &&
        formatInTimeZone(now, DIAL_BLOCK_ET_ZONE, "HH:mm") >= "23:59"))
  ) {
    return addDaysToKey(getWeekStartForDay(today), 7);
  }

  return getWeekStartForDay(today);
};

/**
 * Monday of the ET week holding `now`, Sunday included. A viewed week after
 * it is a "future week", where Copy Last Week is offered (CC).
 */
export const getCalendarWeekStart = (now: Date): string =>
  getWeekStartForDay(getEtDayKey(now));

/** `?week=` from the URL snapped to its Monday; null if absent or not a date. */
export const parseWeekParam = (value: unknown): string | null => {
  if (typeof value !== "string" || !isValidDayKey(value)) {
    return null;
  }

  return getWeekStartForDay(value);
};

/** Monday to Saturday of a week, and its Sunday with `includeSunday`. */
export const getWeekDays = (
  weekStart: string,
  includeSunday = false
): Array<string> =>
  Array.from({ length: WEEK_DAY_COUNT + (includeSunday ? 1 : 0) }, (_, i) =>
    addDaysToKey(weekStart, i)
  );

/** The same ET wall-clock time `weeks` weeks later (DST-safe, unlike +7×24 h). */
export const shiftByWeeksEt = (start: Date, weeks: number): Date => {
  const { dayKey, minutes } = getEtDayAndMinutes(start);

  return getEtInstant(addDaysToKey(dayKey, weeks * 7), minutes);
};

const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime();

/** Whether a block starting at `start` would overlap one of `blocks`. */
export const overlapsAnyBlock = (
  start: Date,
  blocks: Array<DialBlockI>
): boolean => {
  const end = getDialBlockEnd(start);

  return blocks.some(
    (block) =>
      block.status !== DIAL_BLOCK_STATUSES.CANCELLED &&
      overlaps(start, end, new Date(block.start_time), new Date(block.end_time))
  );
};

export const SLOT_BLOCK_REASONS = {
  PAST: "past",
  TOO_LATE: "too_late",
  NO_CAMPAIGN: "no_campaign",
  OVERLAP: "overlap",
} as const;

export type SlotBlockReasonI =
  (typeof SLOT_BLOCK_REASONS)[keyof typeof SLOT_BLOCK_REASONS];

export const SLOT_BLOCK_REASON_COPY: Record<SlotBlockReasonI, string> = {
  past: "This time has already passed",
  too_late: "A 3.5-hour block starting here would end after 9 PM ET",
  no_campaign: "No campaigns available at this time",
  overlap: "This overlaps one of your blocks",
};

/**
 * Whether a block starting `minutes` past ET midnight on `dayKey` fits the
 * campaign's calling days and hours (Command Center's slot check, without
 * capacity). Without a day list the server decides.
 */
export const fitsCampaignWindow = (
  campaign: DialBlockCampaignI,
  dayKey: string,
  minutes: number
): boolean =>
  (!campaign.call_days || campaign.call_days.includes(getIsoWeekday(dayKey))) &&
  minutes >= campaign.dialing_start_hour_et * 60 &&
  minutes + DIAL_BLOCK_LENGTH_MINUTES <= campaign.dialing_end_hour_et * 60;

/**
 * Why an empty slot can't be booked, from what the browser knows for sure,
 * or null when it can be tried. With the caller's campaigns (B2b), a slot no
 * campaign's days and hours fit is refused too, as in Command Center; an
 * empty list blocks nothing, so the dialog can say why. Capacity and the
 * rest are the server's to refuse.
 */
export const getSlotBlockReason = (
  start: Date,
  minutes: number,
  blocks: Array<DialBlockI>,
  now: Date,
  campaigns?: Array<DialBlockCampaignI>
): SlotBlockReasonI | null => {
  if (start.getTime() <= now.getTime()) {
    return SLOT_BLOCK_REASONS.PAST;
  }

  if (minutes > LAST_START_MINUTES) {
    return SLOT_BLOCK_REASONS.TOO_LATE;
  }

  // A Sunday only for a campaign that takes Sunday blocks (C-16 S8): never
  // when the campaigns' days are unknown (the column may be there only for a
  // block booked before its campaign stopped taking Sundays).
  if (
    getIsoWeekday(getEtDayKey(start)) === 7 &&
    !takesSundayBlocks(campaigns)
  ) {
    return SLOT_BLOCK_REASONS.NO_CAMPAIGN;
  }

  if (campaigns && campaigns.length > 0) {
    const dayKey = getEtDayKey(start);

    if (
      !campaigns.some((campaign) =>
        fitsCampaignWindow(campaign, dayKey, minutes)
      )
    ) {
      return SLOT_BLOCK_REASONS.NO_CAMPAIGN;
    }
  }

  if (overlapsAnyBlock(start, blocks)) {
    return SLOT_BLOCK_REASONS.OVERLAP;
  }

  return null;
};

/**
 * Why `block` can't move to start at `start` (`minutes` past ET midnight), or
 * null: the slot rules, with the block out of the way of its own old span.
 */
export const getMoveBlockReason = (
  block: DialBlockI,
  start: Date,
  minutes: number,
  blocks: Array<DialBlockI>,
  now: Date,
  campaigns?: Array<DialBlockCampaignI>
): SlotBlockReasonI | null =>
  getSlotBlockReason(
    start,
    minutes,
    blocks.filter((other) => other.id !== block.id),
    now,
    campaigns
  );

/** A scheduled block that hasn't started: the only kind a caller can cancel. */
export const isCancellable = (block: DialBlockI, now: Date): boolean =>
  block.status === DIAL_BLOCK_STATUSES.SCHEDULED &&
  Date.parse(block.start_time) > now.getTime();

/** Clear Week cancels scheduled blocks starting more than 30 minutes out. */
export const getClearableBlocks = (
  blocks: Array<DialBlockI>,
  now: Date
): Array<DialBlockI> =>
  blocks.filter(
    (block) =>
      block.status === DIAL_BLOCK_STATUSES.SCHEDULED &&
      Date.parse(block.start_time) >
        now.getTime() + CLEAR_WEEK_BUFFER_MINUTES * 60 * 1000
  );

/**
 * How many of the four US zones are in business hours (9 to 5 local) at an
 * ET hour, as Command Center's grid shows it.
 */
export const getUsZoneCoverage = (etHour: number): number => {
  if (etHour >= 12 && etHour <= 16) {
    return 4;
  }

  if (etHour === 11 || etHour === 17) {
    return 3;
  }

  if (etHour === 10 || etHour === 18) {
    return 2;
  }

  if (etHour === 9 || etHour === 19) {
    return 1;
  }

  return 0;
};
